import axios from 'axios';
import Sentry from '../errors/sentry';

const CSRF_URL = '/sanctum/csrf-cookie';

export class ApiError extends Error {
	constructor({ status, code, message, fieldErrors, original }) {
		super(message);
		this.name = 'ApiError';
		this.status = status;
		this.code = code;
		this.fieldErrors = fieldErrors;
		this.original = original;
	}

	// Passthrough to the underlying AxiosError for callers in legacy files that
	// still read `err.response.data` in catch blocks. Migrate to `err.fieldErrors`
	// and `err.message` opportunistically when touching those files.
	get response() {
		return this.original?.response;
	}
}

export const normalizeError = (error) => {
	const response = error?.response;
	const data = response?.data;
	return {
		status: response?.status ?? 0,
		code: data?.code ?? error?.code ?? null,
		message: data?.message ?? error?.message ?? 'Request failed',
		fieldErrors: data?.errors ?? null,
		original: error,
	};
};

const requestMethod = (config) => (config?.method ?? 'get').toUpperCase();

export const breadcrumbRequest = (config) => {
	Sentry.addBreadcrumb({
		category: 'api.request',
		level: 'info',
		message: `${requestMethod(config)} ${config.url}`,
		data: { params: config.params },
	});
	return config;
};

export const breadcrumbResponseSuccess = (response) => {
	Sentry.addBreadcrumb({
		category: 'api.response',
		level: 'info',
		message: `${response.status} ${requestMethod(response.config)} ${response.config?.url}`,
	});
	return response.data;
};

const logoutAndRedirect = () => {
	localStorage.removeItem('user');
	// EN-960 belt-and-braces: this hard-navigates, which tears the Sentry client down anyway, and
	// beforeSend re-derives identity from localStorage on every event. Clearing the scope keeps the
	// two logout transports honest if either of those ever changes.
	Sentry.setUser(null);
	window.location.href = '/login';
};

export const makeResponseErrorHandler = (instance) => async (error) => {
	const status = error?.response?.status ?? 0;
	const url = error?.config?.url;
	const method = requestMethod(error?.config);

	Sentry.addBreadcrumb({
		category: 'api.response',
		level: 'error',
		message: `${status || 'NETWORK_ERROR'} ${method} ${url ?? '<unknown>'}`,
	});

	// /logout returning 401 means the session is already gone, and status 0 means the browser aborted
	// it because the page is already leaving for /login — redirect without retrying or throwing.
	if (url === '/logout' && (status === 401 || status === 0)) {
		logoutAndRedirect();
		return;
	}

	if (status !== 401 || url === '/login' || url === CSRF_URL) {
		throw new ApiError(normalizeError(error));
	}

	if (error.config && !error.config.__isRetry) {
		error.config.__isRetry = true;
		try {
			await instance.get(CSRF_URL);
			return await instance(error.config);
		} catch (retryError) {
			// The retried request's error handler already ran logout if it hit a
			// second 401, and threw an ApiError. Bubble that up unchanged.
			if (retryError instanceof ApiError) {
				throw retryError;
			}
			// Anything else (e.g. CSRF endpoint returning a non-ApiError) falls
			// through to the logout below.
		}
	}

	logoutAndRedirect();
	throw new ApiError(normalizeError(error));
};

const client = axios.create({ withCredentials: true });

client.interceptors.request.use(breadcrumbRequest);
client.interceptors.response.use(breadcrumbResponseSuccess, makeResponseErrorHandler(client));

export default client;
